import axios from 'axios'

// Vuex module: notifications.
// In-app notifications for admin users (/api/admin/notifications*). AppShell
// starts polling the unread count once signed in; the header bell and the
// /notifications page read the inbox from here so both stay in sync.

const POLL_MS = 60 * 1000
const API = '/api/admin/notifications'

let timer = null
let onVisible = null

export default {
  namespaced: true,

  state: () => ({
    unread: 0,
    latestId: 0,
    recent: [],          // latest few for the bell dropdown
    recentLoaded: false,
    // Branch wise: the bell, count and toasts follow the branch the user is
    // working in (sent as X-Loc); the server reports which one that is.
    branch: null,
    branchName: '',
  }),

  mutations: {
    SET_UNREAD(state, n) { state.unread = Math.max(0, Number(n) || 0) },
    SET_BRANCH(state, code) { state.branch = code || null },
    SET_BRANCH_NAME(state, name) { state.branchName = name || '' },
    SET_LATEST(state, id) { state.latestId = Number(id) || 0 },
    SET_RECENT(state, list) { state.recent = list || []; state.recentLoaded = true },
    MARK_READ(state, id) {
      const item = state.recent.find((n) => n.id === id)
      if (item) item.read = true
    },
    MARK_ALL_READ(state) {
      state.recent.forEach((n) => { n.read = true })
      state.unread = 0
    },
    REMOVE(state, id) { state.recent = state.recent.filter((n) => n.id !== id) },
    RESET(state) {
      state.unread = 0
      state.latestId = 0
      state.recent = []
      state.recentLoaded = false
      state.branch = null
      state.branchName = ''
    },
  },

  actions: {
    // Cheap poll. When a newer notification arrives, refresh the dropdown
    // list and pop a toast for it.
    async refreshCount({ state, commit, dispatch }) {
      try {
        const { data } = await axios.get(`${API}/unread-count`)
        if (!data?.success) return
        // Switched branch: show the new branch's inbox without toasting its
        // backlog as if it had just arrived.
        const switched = (data.branch || null) !== state.branch
        const previous = switched ? 0 : state.latestId
        commit('SET_BRANCH', data.branch)
        commit('SET_UNREAD', data.unread)
        commit('SET_LATEST', data.latest_id)
        if (switched || data.latest_id > previous) {
          await dispatch('fetchRecent')
          if (previous) dispatch('toastNewerThan', previous)
        }
      } catch {
        // offline / signed out — try again on the next tick
      }
    },

    async fetchRecent({ commit }) {
      const { data } = await axios.get(API, { params: { per_page: 8 } })
      if (data?.success) {
        commit('SET_RECENT', data.data)
        commit('SET_UNREAD', data.unread)
        const current = (data.branches || []).find((b) => b.code === data.current)
        commit('SET_BRANCH_NAME', current ? current.name : '')
      }
    },

    toastNewerThan({ state }, id) {
      const vm = window.__vueRoot__
      if (!vm || !vm.$notify) return
      const fresh = state.recent.filter((n) => n.id > id && !n.read)
      const type = { danger: 'error', warning: 'warn', success: 'success' }
      fresh.slice(0, 3).forEach((n) => {
        vm.$notify({ group: 'default', type: type[n.level] || 'info', title: n.title, text: n.body || '', duration: 6000 })
      })
    },

    async markRead({ commit }, id) {
      commit('MARK_READ', id)
      const { data } = await axios.post(`${API}/${id}/read`)
      if (data?.success) commit('SET_UNREAD', data.unread)
    },

    // branch: 'current' (the bell), 'all', or a branch code (inbox page view).
    async markAllRead({ commit }, branch = 'current') {
      if (branch === 'current' || branch === 'all') commit('MARK_ALL_READ')
      const { data } = await axios.post(`${API}/read-all`, { branch })
      if (data?.success) commit('SET_UNREAD', data.unread)
    },

    async dismiss({ commit }, id) {
      commit('REMOVE', id)
      const { data } = await axios.post(`${API}/${id}/delete`)
      if (data?.success) commit('SET_UNREAD', data.unread)
    },

    startPolling({ dispatch }) {
      if (timer) return
      dispatch('refreshCount')
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') dispatch('refreshCount')
      }, POLL_MS)
      // Catch up as soon as the app / tab comes back to the foreground.
      onVisible = () => { if (document.visibilityState === 'visible') dispatch('refreshCount') }
      document.addEventListener('visibilitychange', onVisible)
    },

    stopPolling({ commit }) {
      if (timer) clearInterval(timer)
      if (onVisible) document.removeEventListener('visibilitychange', onVisible)
      timer = null
      onVisible = null
      commit('RESET')
    },
  },
}
